/* __next_internal_client_entry_do_not_use__ default auto */ import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import composeClasses from '@mui/utils/composeClasses';
import { alpha } from '@mui/system/colorManipulator';
import CancelIcon from "../internal/svg-icons/Cancel.js";
import useForkRef from "../utils/useForkRef.js";
import unsupportedProp from "../utils/unsupportedProp.js";
import capitalize from "../utils/capitalize.js";
import ButtonBase from "../ButtonBase/index.js";
import { styled } from "../zero-styled/index.js";
import memoTheme from "../utils/memoTheme.js";
import createSimplePaletteValueFilter from "../utils/createSimplePaletteValueFilter.js";
import { useDefaultProps } from "../DefaultPropsProvider/index.js";
import chipClasses, { getChipUtilityClass } from "./chipClasses.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const useUtilityClasses = (ownerState)=>{
    const { classes, disabled, size, color, iconColor, onDelete, clickable, variant } = ownerState;
    const slots = {
        root: [
            'root',
            variant,
            disabled && 'disabled',
            "size".concat(capitalize(size)),
            "color".concat(capitalize(color)),
            clickable && 'clickable',
            clickable && "clickableColor".concat(capitalize(color)),
            onDelete && 'deletable',
            onDelete && "deletableColor".concat(capitalize(color)),
            "".concat(variant).concat(capitalize(color))
        ],
        label: [
            'label',
            "label".concat(capitalize(size))
        ],
        avatar: [
            'avatar',
            "avatar".concat(capitalize(size)),
            "avatarColor".concat(capitalize(color))
        ],
        icon: [
            'icon',
            "icon".concat(capitalize(size)),
            "iconColor".concat(capitalize(iconColor))
        ],
        deleteIcon: [
            'deleteIcon',
            "deleteIcon".concat(capitalize(size)),
            "deleteIconColor".concat(capitalize(color)),
            "deleteIcon".concat(capitalize(variant), "Color").concat(capitalize(color))
        ]
    };
    return composeClasses(slots, getChipUtilityClass, classes);
};
const ChipRoot = styled('div', {
    name: 'MuiChip',
    slot: 'Root',
    overridesResolver: (props, styles)=>{
        const { ownerState } = props;
        const { color, iconColor, clickable, onDelete, size, variant } = ownerState;
        return [
            {
                ["& .".concat(chipClasses.avatar)]: styles.avatar
            },
            {
                ["& .".concat(chipClasses.avatar)]: styles["avatar".concat(capitalize(size))]
            },
            {
                ["& .".concat(chipClasses.avatar)]: styles["avatarColor".concat(capitalize(color))]
            },
            {
                ["& .".concat(chipClasses.icon)]: styles.icon
            },
            {
                ["& .".concat(chipClasses.icon)]: styles["icon".concat(capitalize(size))]
            },
            {
                ["& .".concat(chipClasses.icon)]: styles["iconColor".concat(capitalize(iconColor))]
            },
            {
                ["& .".concat(chipClasses.deleteIcon)]: styles.deleteIcon
            },
            {
                ["& .".concat(chipClasses.deleteIcon)]: styles["deleteIcon".concat(capitalize(size))]
            },
            {
                ["& .".concat(chipClasses.deleteIcon)]: styles["deleteIconColor".concat(capitalize(color))]
            },
            {
                ["& .".concat(chipClasses.deleteIcon)]: styles["deleteIcon".concat(capitalize(variant), "Color").concat(capitalize(color))]
            },
            styles.root,
            styles["size".concat(capitalize(size))],
            styles["color".concat(capitalize(color))],
            clickable && styles.clickable,
            clickable && color !== 'default' && styles["clickableColor".concat(capitalize(color), ")")],
            onDelete && styles.deletable,
            onDelete && color !== 'default' && styles["deletableColor".concat(capitalize(color))],
            styles[variant],
            styles["".concat(variant).concat(capitalize(color))]
        ];
    }
})(memoTheme((param)=>{
    let { theme } = param;
    const textColor = theme.palette.mode === 'light' ? theme.palette.grey[700] : theme.palette.grey[300];
    return {
        maxWidth: '100%',
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.pxToRem(13),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 32,
        color: (theme.vars || theme).palette.text.primary,
        backgroundColor: (theme.vars || theme).palette.action.selected,
        borderRadius: 32 / 2,
        whiteSpace: 'nowrap',
        transition: theme.transitions.create([
            'background-color',
            'box-shadow'
        ]),
        // reset cursor explicitly in case ButtonBase is used
        cursor: 'unset',
        // We disable the focus ring for mouse, touch and keyboard users.
        outline: 0,
        textDecoration: 'none',
        border: 0,
        // Remove `button` border
        padding: 0,
        // Remove `button` padding
        verticalAlign: 'middle',
        boxSizing: 'border-box',
        ["&.".concat(chipClasses.disabled)]: {
            opacity: (theme.vars || theme).palette.action.disabledOpacity,
            pointerEvents: 'none'
        },
        ["& .".concat(chipClasses.avatar)]: {
            marginLeft: 5,
            marginRight: -6,
            width: 24,
            height: 24,
            color: theme.vars ? theme.vars.palette.Chip.defaultAvatarColor : textColor,
            fontSize: theme.typography.pxToRem(12)
        },
        ["& .".concat(chipClasses.avatarColorPrimary)]: {
            color: (theme.vars || theme).palette.primary.contrastText,
            backgroundColor: (theme.vars || theme).palette.primary.dark
        },
        ["& .".concat(chipClasses.avatarColorSecondary)]: {
            color: (theme.vars || theme).palette.secondary.contrastText,
            backgroundColor: (theme.vars || theme).palette.secondary.dark
        },
        ["& .".concat(chipClasses.avatarSmall)]: {
            marginLeft: 4,
            marginRight: -4,
            width: 18,
            height: 18,
            fontSize: theme.typography.pxToRem(10)
        },
        ["& .".concat(chipClasses.icon)]: {
            marginLeft: 5,
            marginRight: -6
        },
        ["& .".concat(chipClasses.deleteIcon)]: {
            WebkitTapHighlightColor: 'transparent',
            color: theme.vars ? "rgba(".concat(theme.vars.palette.text.primaryChannel, " / 0.26)") : alpha(theme.palette.text.primary, 0.26),
            fontSize: 22,
            cursor: 'pointer',
            margin: '0 5px 0 -6px',
            '&:hover': {
                color: theme.vars ? "rgba(".concat(theme.vars.palette.text.primaryChannel, " / 0.4)") : alpha(theme.palette.text.primary, 0.4)
            }
        },
        variants: [
            {
                props: {
                    size: 'small'
                },
                style: {
                    height: 24,
                    ["& .".concat(chipClasses.icon)]: {
                        fontSize: 18,
                        marginLeft: 4,
                        marginRight: -4
                    },
                    ["& .".concat(chipClasses.deleteIcon)]: {
                        fontSize: 16,
                        marginRight: 4,
                        marginLeft: -4
                    }
                }
            },
            ...Object.entries(theme.palette).filter(createSimplePaletteValueFilter([
                'contrastText'
            ])).map((param)=>{
                let [color] = param;
                return {
                    props: {
                        color
                    },
                    style: {
                        backgroundColor: (theme.vars || theme).palette[color].main,
                        color: (theme.vars || theme).palette[color].contrastText,
                        ["& .".concat(chipClasses.deleteIcon)]: {
                            color: theme.vars ? "rgba(".concat(theme.vars.palette[color].contrastTextChannel, " / 0.7)") : alpha(theme.palette[color].contrastText, 0.7),
                            '&:hover, &:active': {
                                color: (theme.vars || theme).palette[color].contrastText
                            }
                        }
                    }
                };
            }),
            {
                props: (props)=>props.iconColor === props.color,
                style: {
                    ["& .".concat(chipClasses.icon)]: {
                        color: theme.vars ? theme.vars.palette.Chip.defaultIconColor : textColor
                    }
                }
            },
            {
                props: (props)=>props.iconColor === props.color && props.color !== 'default',
                style: {
                    ["& .".concat(chipClasses.icon)]: {
                        color: 'inherit'
                    }
                }
            },
            {
                props: {
                    onDelete: true
                },
                style: {
                    ["&.".concat(chipClasses.focusVisible)]: {
                        backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette.action.selectedChannel, " / calc(").concat(theme.vars.palette.action.selectedOpacity, " + ").concat(theme.vars.palette.action.focusOpacity, "))") : alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity + theme.palette.action.focusOpacity)
                    }
                }
            },
            ...Object.entries(theme.palette).filter(createSimplePaletteValueFilter([
                'dark'
            ])).map((param)=>{
                let [color] = param;
                return {
                    props: {
                        color,
                        onDelete: true
                    },
                    style: {
                        ["&.".concat(chipClasses.focusVisible)]: {
                            background: (theme.vars || theme).palette[color].dark
                        }
                    }
                };
            }),
            {
                props: {
                    clickable: true
                },
                style: {
                    userSelect: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette.action.selectedChannel, " / calc(").concat(theme.vars.palette.action.selectedOpacity, " + ").concat(theme.vars.palette.action.hoverOpacity, "))") : alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity)
                    },
                    ["&.".concat(chipClasses.focusVisible)]: {
                        backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette.action.selectedChannel, " / calc(").concat(theme.vars.palette.action.selectedOpacity, " + ").concat(theme.vars.palette.action.focusOpacity, "))") : alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity + theme.palette.action.focusOpacity)
                    },
                    '&:active': {
                        boxShadow: (theme.vars || theme).shadows[1]
                    }
                }
            },
            ...Object.entries(theme.palette).filter(createSimplePaletteValueFilter([
                'dark'
            ])).map((param)=>{
                let [color] = param;
                return {
                    props: {
                        color,
                        clickable: true
                    },
                    style: {
                        ["&:hover, &.".concat(chipClasses.focusVisible)]: {
                            backgroundColor: (theme.vars || theme).palette[color].dark
                        }
                    }
                };
            }),
            {
                props: {
                    variant: 'outlined'
                },
                style: {
                    backgroundColor: 'transparent',
                    border: theme.vars ? "1px solid ".concat(theme.vars.palette.Chip.defaultBorder) : "1px solid ".concat(theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[700]),
                    ["&.".concat(chipClasses.clickable, ":hover")]: {
                        backgroundColor: (theme.vars || theme).palette.action.hover
                    },
                    ["&.".concat(chipClasses.focusVisible)]: {
                        backgroundColor: (theme.vars || theme).palette.action.focus
                    },
                    ["& .".concat(chipClasses.avatar)]: {
                        marginLeft: 4
                    },
                    ["& .".concat(chipClasses.avatarSmall)]: {
                        marginLeft: 2
                    },
                    ["& .".concat(chipClasses.icon)]: {
                        marginLeft: 4
                    },
                    ["& .".concat(chipClasses.iconSmall)]: {
                        marginLeft: 2
                    },
                    ["& .".concat(chipClasses.deleteIcon)]: {
                        marginRight: 5
                    },
                    ["& .".concat(chipClasses.deleteIconSmall)]: {
                        marginRight: 3
                    }
                }
            },
            ...Object.entries(theme.palette).filter(createSimplePaletteValueFilter()) // no need to check for mainChannel as it's calculated from main
            .map((param)=>{
                let [color] = param;
                return {
                    props: {
                        variant: 'outlined',
                        color
                    },
                    style: {
                        color: (theme.vars || theme).palette[color].main,
                        border: "1px solid ".concat(theme.vars ? "rgba(".concat(theme.vars.palette[color].mainChannel, " / 0.7)") : alpha(theme.palette[color].main, 0.7)),
                        ["&.".concat(chipClasses.clickable, ":hover")]: {
                            backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette[color].mainChannel, " / ").concat(theme.vars.palette.action.hoverOpacity, ")") : alpha(theme.palette[color].main, theme.palette.action.hoverOpacity)
                        },
                        ["&.".concat(chipClasses.focusVisible)]: {
                            backgroundColor: theme.vars ? "rgba(".concat(theme.vars.palette[color].mainChannel, " / ").concat(theme.vars.palette.action.focusOpacity, ")") : alpha(theme.palette[color].main, theme.palette.action.focusOpacity)
                        },
                        ["& .".concat(chipClasses.deleteIcon)]: {
                            color: theme.vars ? "rgba(".concat(theme.vars.palette[color].mainChannel, " / 0.7)") : alpha(theme.palette[color].main, 0.7),
                            '&:hover, &:active': {
                                color: (theme.vars || theme).palette[color].main
                            }
                        }
                    }
                };
            })
        ]
    };
}));
const ChipLabel = styled('span', {
    name: 'MuiChip',
    slot: 'Label',
    overridesResolver: (props, styles)=>{
        const { ownerState } = props;
        const { size } = ownerState;
        return [
            styles.label,
            styles["label".concat(capitalize(size))]
        ];
    }
})({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingLeft: 12,
    paddingRight: 12,
    whiteSpace: 'nowrap',
    variants: [
        {
            props: {
                variant: 'outlined'
            },
            style: {
                paddingLeft: 11,
                paddingRight: 11
            }
        },
        {
            props: {
                size: 'small'
            },
            style: {
                paddingLeft: 8,
                paddingRight: 8
            }
        },
        {
            props: {
                size: 'small',
                variant: 'outlined'
            },
            style: {
                paddingLeft: 7,
                paddingRight: 7
            }
        }
    ]
});
function isDeleteKeyboardEvent(keyboardEvent) {
    return keyboardEvent.key === 'Backspace' || keyboardEvent.key === 'Delete';
}
/**
 * Chips represent complex entities in small blocks, such as a contact.
 */ const Chip = /*#__PURE__*/ React.forwardRef(function Chip(inProps, ref) {
    const props = useDefaultProps({
        props: inProps,
        name: 'MuiChip'
    });
    const { avatar: avatarProp, className, clickable: clickableProp, color = 'default', component: ComponentProp, deleteIcon: deleteIconProp, disabled = false, icon: iconProp, label, onClick, onDelete, onKeyDown, onKeyUp, size = 'medium', variant = 'filled', tabIndex, skipFocusWhenDisabled = false, // TODO v6: Rename to `focusableWhenDisabled`.
    ...other } = props;
    const chipRef = React.useRef(null);
    const handleRef = useForkRef(chipRef, ref);
    const handleDeleteIconClick = (event)=>{
        // Stop the event from bubbling up to the `Chip`
        event.stopPropagation();
        if (onDelete) {
            onDelete(event);
        }
    };
    const handleKeyDown = (event)=>{
        // Ignore events from children of `Chip`.
        if (event.currentTarget === event.target && isDeleteKeyboardEvent(event)) {
            // Will be handled in keyUp, otherwise some browsers
            // might init navigation
            event.preventDefault();
        }
        if (onKeyDown) {
            onKeyDown(event);
        }
    };
    const handleKeyUp = (event)=>{
        // Ignore events from children of `Chip`.
        if (event.currentTarget === event.target) {
            if (onDelete && isDeleteKeyboardEvent(event)) {
                onDelete(event);
            }
        }
        if (onKeyUp) {
            onKeyUp(event);
        }
    };
    const clickable = clickableProp !== false && onClick ? true : clickableProp;
    const component = clickable || onDelete ? ButtonBase : ComponentProp || 'div';
    const ownerState = {
        ...props,
        component,
        disabled,
        size,
        color,
        iconColor: /*#__PURE__*/ React.isValidElement(iconProp) ? iconProp.props.color || color : color,
        onDelete: !!onDelete,
        clickable,
        variant
    };
    const classes = useUtilityClasses(ownerState);
    const moreProps = component === ButtonBase ? {
        component: ComponentProp || 'div',
        focusVisibleClassName: classes.focusVisible,
        ...onDelete && {
            disableRipple: true
        }
    } : {};
    let deleteIcon = null;
    if (onDelete) {
        deleteIcon = deleteIconProp && /*#__PURE__*/ React.isValidElement(deleteIconProp) ? /*#__PURE__*/ React.cloneElement(deleteIconProp, {
            className: clsx(deleteIconProp.props.className, classes.deleteIcon),
            onClick: handleDeleteIconClick
        }) : /*#__PURE__*/ _jsx(CancelIcon, {
            className: clsx(classes.deleteIcon),
            onClick: handleDeleteIconClick
        });
    }
    let avatar = null;
    if (avatarProp && /*#__PURE__*/ React.isValidElement(avatarProp)) {
        avatar = /*#__PURE__*/ React.cloneElement(avatarProp, {
            className: clsx(classes.avatar, avatarProp.props.className)
        });
    }
    let icon = null;
    if (iconProp && /*#__PURE__*/ React.isValidElement(iconProp)) {
        icon = /*#__PURE__*/ React.cloneElement(iconProp, {
            className: clsx(classes.icon, iconProp.props.className)
        });
    }
    if ("production" !== 'production') {
        if (avatar && icon) {
            console.error('MUI: The Chip component can not handle the avatar ' + 'and the icon prop at the same time. Pick one.');
        }
    }
    return /*#__PURE__*/ _jsxs(ChipRoot, {
        as: component,
        className: clsx(classes.root, className),
        disabled: clickable && disabled ? true : undefined,
        onClick: onClick,
        onKeyDown: handleKeyDown,
        onKeyUp: handleKeyUp,
        ref: handleRef,
        tabIndex: skipFocusWhenDisabled && disabled ? -1 : tabIndex,
        ownerState: ownerState,
        ...moreProps,
        ...other,
        children: [
            avatar || icon,
            /*#__PURE__*/ _jsx(ChipLabel, {
                className: clsx(classes.label),
                ownerState: ownerState,
                children: label
            }),
            deleteIcon
        ]
    });
});
"production" !== "production" ? Chip.propTypes = {
    // ┌────────────────────────────── Warning ──────────────────────────────┐
    // │ These PropTypes are generated from the TypeScript type definitions. │
    // │    To update them, edit the d.ts file and run `pnpm proptypes`.     │
    // └─────────────────────────────────────────────────────────────────────┘
    /**
   * The Avatar element to display.
   */ avatar: PropTypes.element,
    /**
   * This prop isn't supported.
   * Use the `component` prop if you need to change the children structure.
   */ children: unsupportedProp,
    /**
   * Override or extend the styles applied to the component.
   */ classes: PropTypes.object,
    /**
   * @ignore
   */ className: PropTypes.string,
    /**
   * If `true`, the chip will appear clickable, and will raise when pressed,
   * even if the onClick prop is not defined.
   * If `false`, the chip will not appear clickable, even if onClick prop is defined.
   * This can be used, for example,
   * along with the component prop to indicate an anchor Chip is clickable.
   * Note: this controls the UI and does not affect the onClick event.
   */ clickable: PropTypes.bool,
    /**
   * The color of the component.
   * It supports both default and custom theme colors, which can be added as shown in the
   * [palette customization guide](https://mui.com/material-ui/customization/palette/#custom-colors).
   * @default 'default'
   */ color: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'default',
            'primary',
            'secondary',
            'error',
            'info',
            'success',
            'warning'
        ]),
        PropTypes.string
    ]),
    /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */ component: PropTypes.elementType,
    /**
   * Override the default delete icon element. Shown only if `onDelete` is set.
   */ deleteIcon: PropTypes.element,
    /**
   * If `true`, the component is disabled.
   * @default false
   */ disabled: PropTypes.bool,
    /**
   * Icon element.
   */ icon: PropTypes.element,
    /**
   * The content of the component.
   */ label: PropTypes.node,
    /**
   * @ignore
   */ onClick: PropTypes.func,
    /**
   * Callback fired when the delete icon is clicked.
   * If set, the delete icon will be shown.
   */ onDelete: PropTypes.func,
    /**
   * @ignore
   */ onKeyDown: PropTypes.func,
    /**
   * @ignore
   */ onKeyUp: PropTypes.func,
    /**
   * The size of the component.
   * @default 'medium'
   */ size: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'medium',
            'small'
        ]),
        PropTypes.string
    ]),
    /**
   * If `true`, allows the disabled chip to escape focus.
   * If `false`, allows the disabled chip to receive focus.
   * @default false
   */ skipFocusWhenDisabled: PropTypes.bool,
    /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */ sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object,
            PropTypes.bool
        ])),
        PropTypes.func,
        PropTypes.object
    ]),
    /**
   * @ignore
   */ tabIndex: PropTypes.number,
    /**
   * The variant to use.
   * @default 'filled'
   */ variant: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.oneOf([
            'filled',
            'outlined'
        ]),
        PropTypes.string
    ])
} : void 0;
export default Chip;
